import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

interface User {
  _id: string;
  name: string;
  email: string;
  role: string;
  createdAt: string;
  walletBalanceEGP?: number;
}

export default function UsersTable() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState(''); // The search term actually used for API calls
  const [roleFilter, setRoleFilter] = useState<'all' | 'user' | 'admin'>('all');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(20);

  const [walletUser, setWalletUser] = useState<User | null>(null);
  const [walletAmount, setWalletAmount] = useState('');
  const [walletNote, setWalletNote] = useState('');
  const [walletSubmitting, setWalletSubmitting] = useState(false);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
  }, [activeSearch, roleFilter]);

  useEffect(() => {
    loadUsers();
  }, [currentPage, limit, activeSearch, roleFilter]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const params: any = { 
        page: currentPage,
        limit: limit,
      };
      
      if (activeSearch) params.search = activeSearch;
      if (roleFilter !== 'all') params.role = roleFilter;
      
      const response = await api.get('/admin/users', { params });
      setUsers(response.data.data || []);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setActiveSearch(search);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const clearFilters = () => {
    setSearch('');
    setActiveSearch('');
    setRoleFilter('all');
    setCurrentPage(1);
  };

  const updateUserRole = async (id: string, newRole: string) => {
    try {
      await api.put(`/admin/users/${id}/role`, { role: newRole });
      loadUsers();
    } catch (error) {
      console.error('Error updating user role:', error);
      alert(t('users.failedToUpdateRole'));
    }
  };

  const openAddToWallet = (user: User) => {
    setWalletUser(user);
    setWalletAmount('');
    setWalletNote('');
  };

  const submitAddToWallet = async () => {
    if (!walletUser) return;
    const amount = parseFloat(walletAmount);
    if (isNaN(amount) || amount <= 0) {
      alert(t('users.invalidAmount'));
      return;
    }
    try {
      setWalletSubmitting(true);
      await api.post(`/admin/users/${walletUser._id}/wallet/add`, {
        amountEGP: amount,
        note: walletNote.trim() || undefined,
      });
      setWalletUser(null);
      loadUsers();
    } catch (error: any) {
      console.error('Error adding to wallet:', error);
      alert(error.response?.data?.message || t('users.failedToAddToWallet'));
    } finally {
      setWalletSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-orange"></div>
        <p className="mt-4 text-gray-600">{t('users.loading')}</p>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${isRtl ? 'text-right' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className={`flex flex-col md:flex-row gap-4 ${isRtl ? 'md:flex-row-reverse' : ''}`}>
          <div className="flex-1 relative flex items-center">
            <MagnifyingGlassIcon className={`absolute w-5 h-5 text-gray-400 ${isRtl ? 'right-3 left-auto' : 'left-3'}`} />
            <input
              type="text"
              placeholder={t('users.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={handleKeyPress}
              className={`w-full py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent ${isRtl ? 'pr-10 pl-20' : 'pl-10 pr-20'}`}
            />
            <button
              onClick={handleSearch}
              className={`absolute px-4 py-1.5 bg-primary-orange hover:bg-orange-600 text-white rounded-lg text-sm transition-colors ${isRtl ? 'left-2 right-auto' : 'right-2'}`}
            >
              {t('common.search')}
            </button>
          </div>
          <div className={`flex gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value as any)}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
            >
              <option value="all">{t('users.allRoles')}</option>
              <option value="user">{t('users.roleUser')}</option>
              <option value="admin">{t('users.roleAdmin')}</option>
            </select>
            {(search || roleFilter !== 'all') && (
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {t('common.clearFilters')}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('users.name')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('users.email')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('users.role')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('users.wallet')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('users.createdAt')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('users.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{user.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.role === 'admin'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {user.role === 'admin' ? t('users.roleAdmin') : t('users.roleUser')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {typeof user.walletBalanceEGP === 'number' ? `${user.walletBalanceEGP.toFixed(2)} EGP` : '0.00 EGP'}
                    </div>
                    <button
                      type="button"
                      onClick={() => openAddToWallet(user)}
                      className="text-xs text-primary-orange hover:underline mt-0.5"
                    >
                      {t('users.addToWallet')}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <select
                      value={user.role}
                      onChange={(e) => updateUserRole(user._id, e.target.value)}
                      className="border rounded-lg px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-orange"
                    >
                      <option value="user">{t('users.roleUser')}</option>
                      <option value="admin">{t('users.roleAdmin')}</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {users.length === 0 && (
          <div className="text-center py-8 text-gray-500">{t('users.noUsers')}</div>
        )}
        
        {totalPages > 1 && (
          <div className={`px-6 py-4 border-t border-gray-200 flex items-center justify-between ${isRtl ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
              <span className="text-sm text-gray-700">
                {t('common.showing', { from: ((currentPage - 1) * limit) + 1, to: Math.min(currentPage * limit, total), total })}
              </span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(parseInt(e.target.value));
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
              >
                <option value={10}>10 {t('common.perPage')}</option>
                <option value={20}>20 {t('common.perPage')}</option>
                <option value={50}>50 {t('common.perPage')}</option>
                <option value={100}>100 {t('common.perPage')}</option>
              </select>
            </div>
            <div className={`flex items-center gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                {t('common.previous')}
              </button>
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 rounded-lg text-sm ${
                        currentPage === pageNum
                          ? 'bg-primary-orange text-white'
                          : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        )}
      </div>

      {walletUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className={`relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white ${isRtl ? 'text-right' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
            <h3 className="text-lg font-bold text-gray-900 mb-4">{t('users.addToWallet')}</h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('common.user')}: <strong>{walletUser.name}</strong> ({walletUser.email})
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.amount')}</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={walletAmount}
                  onChange={(e) => setWalletAmount(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                  placeholder="e.g. 50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.note')}</label>
                <input
                  type="text"
                  value={walletNote}
                  onChange={(e) => setWalletNote(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                  placeholder="e.g. Promotional credit"
                />
              </div>
            </div>
            <div className={`mt-6 flex gap-2 ${isRtl ? 'flex-row-reverse justify-start' : 'justify-end'}`}>
              <button
                type="button"
                onClick={() => setWalletUser(null)}
                disabled={walletSubmitting}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={submitAddToWallet}
                disabled={walletSubmitting}
                className="px-4 py-2 bg-primary-orange text-white rounded-lg text-sm hover:bg-orange-600 disabled:opacity-50"
              >
                {walletSubmitting ? t('users.addingToWallet') : t('users.addToWallet')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
